import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, UsersIcon, TrendingUpIcon, GlobeIcon, FileTextIcon, AwardIcon } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
export function About() {
  useSEO({
    title: 'About Us',
    description: 'UK-registered procurement consultancy with over a decade of experience facilitating institutional sourcing and tender support across global markets. Company No. 08400329.',
    path: '/about'
  });
  const commitments = [{
    icon: ShieldCheckIcon,
    title: 'Professionalism',
    description: 'Rigorous standards in supplier verification, documentation, and project management'
  }, {
    icon: UsersIcon,
    title: 'Transparency',
    description: 'Clear communication, honest evaluation, and straightforward commercial terms'
  }, {
    icon: TrendingUpIcon,
    title: 'Efficiency',
    description: 'Streamlined processes that reduce procurement timelines and administrative burden'
  }, {
    icon: GlobeIcon,
    title: 'Long-term Partnership',
    description: 'Building sustainable relationships between suppliers and procurement entities'
  }];
  const values = [{
    title: 'Independent & Impartial',
    description: 'We represent both suppliers and buyers with no conflicts of interest or exclusive arrangements'
  }, {
    title: 'Global Reach, Regional Expertise',
    description: 'Active across five continents with flagship focus on Africa, Asia, and the Middle East'
  }, {
    title: 'Verified Supplier Network',
    description: '500+ vetted manufacturers and exporters across all major sectors and product categories'
  }, {
    title: 'End-to-End Procurement Support',
    description: 'From opportunity identification through to final delivery and contract management'
  }, {
    title: 'UK-Registered & Compliant',
    description: 'Established UK company with full regulatory compliance and transparent operations'
  }, {
    title: 'Multilingual Team',
    description: 'English, French, and regional language capabilities for seamless cross-border coordination'
  }];
  return <div className="pt-24" data-id="element-303">
      {/* Hero Band */}
      <section className="py-20 px-6 bg-navy-light" data-id="element-304">
        <div className="max-w-4xl mx-auto text-center" data-id="element-305">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-306">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-307">
              <div className="w-12 h-px bg-gold" data-id="element-308"></div>
              <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-309">
                Who We Are
              </span>
              <div className="w-12 h-px bg-gold" data-id="element-310"></div>
            </div>
            <h1 className="text-5xl lg:text-6xl font-semibold text-white mb-6" data-id="element-311">
              About 888 Sourcing Ltd
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed" data-id="element-312">
              A UK-based independent procurement consultancy connecting global
              suppliers with institutional and commercial procurement
              opportunities worldwide
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 px-6 bg-navy-dark" data-id="element-313">
        <div className="max-w-7xl mx-auto" data-id="element-314">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16" data-id="element-315">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-316">
              <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-6" data-id="element-317">
                Global Procurement Consultancy with UK Roots
              </h2>
              <div className="space-y-4 text-slate-400 leading-relaxed" data-id="element-318">
                <p data-id="element-319">
                  888 Sourcing Ltd is a UK-registered procurement consultancy
                  and sourcing intermediary with over a decade of experience
                  facilitating large-scale institutional and commercial
                  procurement across global markets.
                </p>
                <p data-id="element-320">
                  We support suppliers and institutional buyers on procurement
                  opportunities relating to multilateral, government-backed, and
                  African Union (AU)-funded projects. Our services span supplier
                  identification, tender coordination, specification evaluation,
                  contract liaison, and logistics management across five
                  continents.
                </p>
                <p data-id="element-321">
                  Operating from our London headquarters, we maintain
                  established networks across Africa, Europe, Asia-Pacific, the
                  Middle East, and the Americas. Our flagship focus is on
                  emerging and high-growth markets, with particular depth in
                  African and Asian procurement landscapes.
                </p>
                <p data-id="element-322">
                  Our business model is commission and fee-based. We represent
                  both suppliers seeking market access and procurement entities
                  requiring reliable sourcing partners, maintaining independence
                  and impartiality throughout all engagements.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-navy-light border-l-4 border-gold p-8 rounded-lg" data-id="element-323">
              <h3 className="text-xl font-semibold text-white mb-6" data-id="element-324">
                Company Credentials
              </h3>
              <div className="space-y-4" data-id="element-325">
                <div className="flex items-start gap-3" data-id="element-326">
                  <FileTextIcon size={20} className="text-gold mt-1 flex-shrink-0" data-id="element-327" />
                  <div data-id="element-328">
                    <div className="text-white font-medium mb-1" data-id="element-329">
                      UK Company Registration
                    </div>
                    <div className="text-slate-400 text-sm" data-id="element-330">
                      Companies House No. 08400329
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-331">
                  <AwardIcon size={20} className="text-gold mt-1 flex-shrink-0" data-id="element-332" />
                  <div data-id="element-333">
                    <div className="text-white font-medium mb-1" data-id="element-334">
                      VAT Registration
                    </div>
                    <div className="text-slate-400 text-sm" data-id="element-335">
                      VAT No. GB158689350
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-336">
                  <GlobeIcon size={20} className="text-gold mt-1 flex-shrink-0" data-id="element-337" />
                  <div data-id="element-338">
                    <div className="text-white font-medium mb-1" data-id="element-339">
                      Headquarters
                    </div>
                    <div className="text-slate-400 text-sm" data-id="element-340">
                      London, United Kingdom
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-341">
                  <TrendingUpIcon size={20} className="text-gold mt-1 flex-shrink-0" data-id="element-342" />
                  <div data-id="element-343">
                    <div className="text-white font-medium mb-1" data-id="element-344">
                      Established
                    </div>
                    <div className="text-slate-400 text-sm" data-id="element-345">
                      2013 (10+ years experience)
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-24 px-6 bg-navy-light" data-id="element-346">
        <div className="max-w-7xl mx-auto" data-id="element-347">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-348">
            <h2 className="text-4xl lg:text-5xl font-semibold text-white mb-4" data-id="element-349">
              Our Commitment
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto" data-id="element-350">
              Four pillars that guide every engagement and partnership
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-351">
            {commitments.map((item, i) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-navy-dark border border-slate-800 p-6 rounded-lg text-center" data-id="element-352">
                <div className="w-12 h-12 bg-gold/10 border border-gold/30 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-353">
                  <item.icon size={24} className="text-gold" data-id="element-354" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-355">
                  {item.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-356">
                  {item.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-6 bg-navy-dark" data-id="element-357">
        <div className="max-w-7xl mx-auto" data-id="element-358">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-359">
            <h2 className="text-4xl lg:text-5xl font-semibold text-white mb-4" data-id="element-360">
              Why Choose 888 Sourcing
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto" data-id="element-361">
              What sets us apart in the procurement consultancy landscape
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-362">
            {values.map((value, i) => <motion.div key={value.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-navy-light border border-slate-800 p-6 rounded-lg" data-id="element-363">
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-364">
                  {value.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-365">
                  {value.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-navy-light" data-id="element-366">
        <div className="max-w-4xl mx-auto text-center" data-id="element-367">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-368">
            <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-6" data-id="element-369">
              Ready to Work with Us?
            </h2>
            <p className="text-slate-400 text-lg mb-8" data-id="element-370">
              Complete our pre-qualification form or contact our team to discuss
              your procurement needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-371">
              <Link to="/supplier-registration" className="bg-gold hover:bg-gold-dark text-navy-dark px-8 py-3 rounded-md font-semibold text-sm transition-colors" data-id="element-372">
                Pre-Qualification Form
              </Link>
              <Link to="/contact" className="border-2 border-slate-700 hover:border-gold text-white px-8 py-3 rounded-md font-semibold text-sm transition-colors" data-id="element-373">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}